import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useVideoPlaylistOptions, useVideoPlaylistStore } from '../store';
import { fetchVideos } from '../store/actions';

import { loadEntityById, loadWebContainerEntities } from '@apis/editorial';
import { fetchMediaAssets } from '@apis/mediaAssets';
import { slugSeparator } from '@cobalt/utilities';
import { uniqBy } from '@utilities/collection-utils';

export const useVideoPlaylist = () => {
  const { options } = useVideoPlaylistOptions();
  const filters = useVideoPlaylistStore((state) => state.filters);
  const filtersActive = !!filters?.team || !!filters?.matchday;

  const {
    data: single,
    isFetched: singleFetched,
    fetchStatus: singleStatus,
  } = useQuery({
    queryKey: ['video-playlist-single', options.selectedVideoId],
    queryFn: ({ signal }) => loadEntityById(options.selectedVideoId.split(slugSeparator).at(0), signal),
    enabled: !!options.selectedVideoId && !filtersActive,
  });
  const singleSuccess = singleFetched || singleStatus === 'idle';

  const {
    data: webContainer,
    isFetched: webContainerFetched,
    fetchStatus: webContainerStatus,
  } = useQuery({
    queryKey: ['video-playlist-web-container', options.webContainer],
    queryFn: ({ signal }) => loadWebContainerEntities(options.webContainer, undefined, signal),
    placeholderData: [],
    enabled: !!options.webContainer && !filtersActive,
  });
  const webContainerSuccess = webContainerFetched || webContainerStatus === 'idle';

  const fixed = useMemo(() => uniqBy([...(single ? [single] : []), ...webContainer], 'id'), [single, webContainer]);

  const {
    data: classification,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['video-playlist', options, filters, fixed.length],
    queryFn: ({ pageParam, signal }) =>
      fetchVideos(
        {
          options,
          filters,
          limit: pageParam === 0 && options.limit > fixed.length ? options.limit - fixed.length : options.limit,
          offset: options.limit * pageParam + fixed.length,
        },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.length ? allPages.length : undefined),
    select: (data) => data.pages.flatMap((page) => page) ?? [],
    enabled: (!!singleSuccess && !!webContainerSuccess && options.limit > fixed.length) || !!filtersActive,
  });

  const videos = useMemo(() => uniqBy([...fixed, ...(classification ?? [])], 'id'), [fixed, classification]);
  const videoIds = useMemo(() => videos.map((r) => r.id), [videos]);

  const { data: masData } = useQuery({
    queryKey: ['mas-videos', videoIds],
    queryFn: ({ signal }) => fetchMediaAssets(videoIds, signal),
    enabled: !!videoIds?.length,
    placeholderData: keepPreviousData,
  });

  const result = useMemo(
    () =>
      videos
        ?.map((r) => ({ ...r, hlsStreamUrl: masData?.find((v) => v?.mediaAssetId === r.id)?.hlsStreamUrl }))
        .filter((r) => !!r.hlsStreamUrl) ?? [],
    [masData, videos],
  );

  return { videos: result, hasNextPage, fetchNextPage };
};
